import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from '../firebase';
import { db } from '../firebase';
import { FileText, Download, X, Calendar } from 'lucide-react';
import { cn } from '../lib/utils';
import html2pdf from 'html2pdf.js';

const getLocalAuthToken = () => localStorage.getItem('app_auth_token');
const isLocalSession = () => Boolean(getLocalAuthToken());

const localApiRequest = async <T,>(path: string): Promise<T> => {
  const response = await fetch(path, { headers: { Authorization: `Bearer ${getLocalAuthToken()}` } });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.error || `Request failed (${response.status})`);
  return payload as T;
};

interface SOAModalProps {
  orgId: string;
  contactId: string;
  contactName: string;
  onClose: () => void;
  type: 'Customer' | 'Vendor';
}

export default function SOAModal({ orgId, contactId, contactName, onClose, type }: SOAModalProps) {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<any[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const fetchSOA = async () => {
      setLoading(true);
      try {
        if (isLocalSession()) {
          const documents = await localApiRequest<any[]>(`/api/organizations/${orgId}/documents?contactId=${encodeURIComponent(contactId)}`);
          const lines: any[] = [];
          documents.forEach(document => {
            if (['Draft', 'Void', 'Deleted', 'Cancelled'].includes(document.status)) return;
            const isReceivable = type === 'Customer' && ['invoice', 'sales_receipt'].includes(document.type);
            const isPayable = type === 'Vendor' && ['bill', 'purchase_order'].includes(document.type);
            if (!isReceivable && !isPayable) return;
            const total = Number(document.totalAmount || 0);
            const paid = Number(document.paymentReceived || 0);
            lines.push({
              date: document.date || document.createdAt,
              description: `${document.type === 'bill' ? 'Bill' : document.type === 'purchase_order' ? 'PO' : document.type === 'sales_receipt' ? 'Sales Receipt' : 'Invoice'} - ${document.number || 'No Ref'}`,
              ref: document.number || '-', debit: type === 'Customer' ? total : 0, credit: type === 'Vendor' ? total : 0
            });
            if (paid > 0) lines.push({
              date: document.date || document.createdAt, description: `Payment - ${document.number || 'No Ref'}`,
              ref: document.number || '-', debit: type === 'Vendor' ? paid : 0, credit: type === 'Customer' ? paid : 0
            });
          });
          const sorted = lines.filter(line => line.date).sort((left, right) => new Date(left.date).getTime() - new Date(right.date).getTime());
          let runningBalance = 0;
          setEntries(sorted.map(line => ({ ...line, balance: (runningBalance += line.debit - line.credit) })));
          setLoading(false);
          return;
        }
        const [docsSnap, journalsSnap, txsSnap, accountsSnap] = await Promise.all([
          getDocs(query(collection(db, 'documents'), where('orgId', '==', orgId))),
          getDocs(query(collection(db, 'manual_journals'), where('orgId', '==', orgId))),
          getDocs(query(collection(db, 'transactions'), where('orgId', '==', orgId))),
          getDocs(query(collection(db, 'chart_of_accounts'), where('orgId', '==', orgId)))
        ]);

        const accounts = accountsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() as any }));
        const relevantAccountIds = accounts.filter(a => {
          if (type === 'Customer') return a.type === 'Accounts Receivable' || a.name.includes('Receivable');
          return a.type === 'Accounts Payable' || a.name.includes('Payable');
        }).map(a => a.id);

        let lines: any[] = [];

        docsSnap.forEach(doc => {
          const d = doc.data();
          if (d.customerId === contactId || d.contactId === contactId) {
            if (d.status !== 'Draft' && d.status !== 'Void') {
              if (type === 'Customer' && (d.type === 'invoice' || d.type === 'sales_receipt')) {
                lines.push({
                  date: d.date || d.issueDate || d.createdAt?.toDate().toISOString(),
                  description: `${d.type === 'invoice' ? 'Invoice' : 'Sales Receipt'} - ${d.number || d.invoiceNumber || 'No Ref'}`,
                  ref: d.number || d.invoiceNumber || '-',
                  debit: d.totalAmount || 0,
                  credit: 0
                });
              } else if (type === 'Vendor' && (d.type === 'bill' || d.type === 'purchase_order')) {
                lines.push({
                  date: d.date || d.issueDate || d.createdAt?.toDate().toISOString(),
                  description: `${d.type === 'bill' ? 'Bill' : 'PO'} - ${d.number || d.billNumber || 'No Ref'}`,
                  ref: d.number || d.billNumber || '-',
                  debit: 0,
                  credit: d.totalAmount || 0
                });
              }
            }
          }
        });

        journalsSnap.forEach(doc => {
          const j = doc.data();
          if (j.status !== 'Draft' && j.lines) {
            (j.lines || []).forEach((line: any) => {
              if (line.contactId === contactId && relevantAccountIds.includes(line.accountId)) {
                lines.push({
                  date: j.date || j.createdAt?.toDate().toISOString(),
                  description: `Manual Journal - ${j.narration || j.journalNumber}`,
                  ref: j.journalNumber || '-',
                  debit: type === 'Customer' ? (line.debit || 0) : (line.credit || 0),
                  credit: type === 'Customer' ? (line.credit || 0) : (line.debit || 0) // AP is credit normal
                });
              }
            });
          }
        });

        txsSnap.forEach(doc => {
          const t = doc.data();
          if (t.contactId === contactId && t.status === 'Completed') {
            lines.push({
              date: t.date || t.createdAt?.toDate().toISOString(),
              description: `Payment / Transaction - ${t.description || t.reference || ''}`,
              ref: t.reference || t.id.substring(0, 8),
              debit: type === 'Customer' ? 0 : (t.type === 'withdrawal' || t.type === 'expense' ? t.amount : 0),
              credit: type === 'Customer' ? (t.type === 'deposit' || t.type === 'income' ? t.amount : 0) : 0
            });
          }
        });

        // Filter valid dates, sort
        const sorted = lines.filter(l => l.date).sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        
        let runningBalance = 0;
        const finalized = sorted.map(l => {
          runningBalance += (l.debit - l.credit);
          return { ...l, balance: runningBalance };
        });

        setEntries(finalized);
        setLoading(false);
      } catch (err) {
        setLoading(false);
      }
    };
    fetchSOA();
  }, [orgId, contactId, type]);

  const filteredEntries = entries.filter(e => {
    if (startDate && new Date(e.date) < new Date(startDate)) return false;
    if (endDate && new Date(e.date) > new Date(endDate)) return false;
    return true;
  });

  const exportExcel = () => {
    if (filteredEntries.length === 0) return;
    const headers = ['Date', 'Description', 'Reference', 'Debit', 'Credit', 'Balance'];
    const csvRows = [
      headers.join(','),
      ...filteredEntries.map(e => [
        new Date(e.date).toLocaleDateString(),
        `"${(e.description||'').replace(/"/g, '""')}"`,
        `"${(e.ref||'').replace(/"/g, '""')}"`,
        e.debit.toFixed(2),
        e.credit.toFixed(2),
        e.balance.toFixed(2)
      ].join(','))
    ];
    const safeContactName = String(contactName || 'Statement').replace(/ /g, '_');
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `SOA_${safeContactName}_${new Date().getTime()}.csv`;
    link.click();
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const exportPDF = () => {
    const element = document.getElementById('soa-content');
    const safeContactName = String(contactName || 'Statement').replace(/ /g, '_');
    const opt = {
      margin: 0.5,
      filename: `SOA_${safeContactName}.pdf`,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' as const }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overscroll-contain p-4 bg-black/50 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden shadow-2xl">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 rounded-t-xl">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Statement of Account</h2>
            <p className="text-sm text-gray-500 mt-1">{contactName} ({type})</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 border-b border-gray-100 flex flex-wrap gap-4 items-end bg-white">
          <div className="space-y-1">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Start Date</label>
            <div className="relative">
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-indigo-500" />
              <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">End Date</label>
            <div className="relative">
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-indigo-500" />
              <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
            </div>
          </div>
          <div className="flex-1" />
          <button onClick={exportExcel} className="px-4 py-2 bg-green-50 text-green-700 hover:bg-green-100 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors">
            <Download className="w-4 h-4" /> Excel
          </button>
          <button onClick={exportPDF} className="px-4 py-2 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors">
            <FileText className="w-4 h-4" /> PDF
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-8 bg-gray-50/30" id="soa-content">
          {loading ? (
            <div className="flex justify-center items-center py-20 text-gray-500">Loading statement...</div>
          ) : (
            <div className="bg-white border text-black border-gray-200 shadow-sm rounded-lg overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h1 className="text-2xl font-bold">Statement of Account</h1>
                <p className="text-gray-500">{contactName}</p>
                <div className="mt-4 text-sm text-gray-600">
                  {startDate && endDate ? `Period: ${startDate} to ${endDate}` : `As of ${new Date().toLocaleDateString()}`}
                </div>
              </div>
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 border-b text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 border-b text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 border-b text-xs font-semibold text-gray-500 uppercase tracking-wider">Reference</th>
                    <th className="px-6 py-3 border-b text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Debit</th>
                    <th className="px-6 py-3 border-b text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Credit</th>
                    <th className="px-6 py-3 border-b text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredEntries.map((e, i) => (
                    <tr key={e.id || `${e.ref}-${i}`} className="hover:bg-gray-50/50">
                      <td className="px-6 py-3 text-sm whitespace-nowrap">{new Date(e.date).toLocaleDateString()}</td>
                      <td className="px-6 py-3 text-sm">{e.description}</td>
                      <td className="px-6 py-3 text-sm font-mono text-gray-500">{e.ref}</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-600">{e.debit ? e.debit.toLocaleString(undefined, { minimumFractionDigits: 2}) : '-'}</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-600">{e.credit ? e.credit.toLocaleString(undefined, { minimumFractionDigits: 2}) : '-'}</td>
                      <td className="px-6 py-3 text-sm text-right font-medium text-gray-900">{e.balance.toLocaleString(undefined, { minimumFractionDigits: 2})}</td>
                    </tr>
                  ))}
                  {filteredEntries.length === 0 && (
                     <tr>
                       <td colSpan={6} className="px-6 py-8 text-center text-sm text-gray-500">No transactions found for this period.</td>
                     </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
